import { v4 as uuidv4 } from "uuid";
import { DEFAULT_BORDER_RADIUS } from "@/features/lomdaEditorV2/components/scene/lib/utils";
import type { LomdaSceneAvatar } from "@/features/lomdaEditorV2/lib/lomdaTypes";
import { centeredBox } from "./transform";
import type {
    ElementTransform,
    MediaElementData,
    SceneElement,
    TextElement,
    MediaElement,
    AvatarElement,
    VerticalAlign,
} from "./types";

export const newElementId = () => uuidv4();

const EMPTY_PARAGRAPH = "<p></p>";

export function createTextElement(
    html: string = EMPTY_PARAGRAPH,
    transform: ElementTransform = centeredBox(70, 44),
    zIndex = 0,
    verticalAlign: VerticalAlign = "top"
): TextElement {
    return {
        id: newElementId(),
        kind: "text",
        html,
        transform,
        zIndex,
        verticalAlign,
    };
}

export function createMediaElement(
    media: Partial<MediaElementData> & { type: "image" | "video"; url: string },
    transform: ElementTransform = centeredBox(42, 56),
    zIndex = 1
): MediaElement {
    return {
        id: newElementId(),
        kind: "media",
        media: {
            objectFit: media.objectFit ?? "cover",
            borderRadius: media.borderRadius ?? DEFAULT_BORDER_RADIUS,
            type: media.type,
            url: media.url,
        },
        transform,
        zIndex,
    };
}

/** Default avatar box: bottom-end, ~18% wide, square. */
export function defaultAvatarTransform(): ElementTransform {
    const width = 18;
    const height = (width * 1920) / 1080; // square in px → taller in %
    return { x: 78, y: 96 - height, width, height, rotation: 0 };
}

export function createAvatarElement(
    avatar: LomdaSceneAvatar,
    transform: ElementTransform = defaultAvatarTransform(),
    zIndex = 1000
): AvatarElement {
    return { id: newElementId(), kind: "avatar", avatar, transform, zIndex };
}

/** Highest zIndex among elements (0 if none) — for placing a new element on top. */
export function topZIndex(elements: SceneElement[]): number {
    return elements.reduce((max, el) => Math.max(max, el.zIndex), 0);
}

/**
 * Paint order for an element. Avatars always render above everything else,
 * regardless of their stored zIndex — the render engine composites the avatar
 * video on the top layer, so the editor matches it and nothing can be placed
 * over the presenter.
 */
const AVATAR_TOP_Z = 1_000_000;
export function paintZIndex(el: SceneElement): number {
    return el.kind === "avatar" ? AVATAR_TOP_Z : el.zIndex;
}
